// frontend/modules/template/components/ContextTransformationForm.tsx
import React, { useState, useEffect } from 'react';
import { Form, Select, Button, Space, Card, Typography, Alert } from 'antd';
import { ContextScriptEditor } from './ContextScriptEditor';
import { 
    useGetAllTextTemplates 
} from '@/shared/api/client/text-template-controller';
import { useContextScripts } from '../hooks/useContextScripts';
import {
    NotificationPlatforms,
    TextTemplateDTO
} from '@/shared/api/model';

interface ContextTransformationFormProps {
    onSubmit: (values: any) => void;
    loading?: boolean;
    initialValues?: any;
    transformationResult?: any;
}

export const ContextTransformationForm: React.FC<ContextTransformationFormProps> = ({
    onSubmit,
    loading = false,
    initialValues,
    transformationResult
}) => {
    const [form] = Form.useForm();
    const [selectedPlatform, setSelectedPlatform] = useState<NotificationPlatforms>();
    const [templates, setTemplates] = useState<TextTemplateDTO[]>([]);
    const [availableContactGroups, setAvailableContactGroups] = useState<Record<string, { label: string, description: string }>>({});
    
    const { scripts } = useContextScripts();
    const { data: templatesResponse, refetch: getTemplates } = useGetAllTextTemplates();
    
    // Fetch templates when platform changes
    useEffect(() => {
        if (selectedPlatform) {
            getTemplates()
                .then(response => {
                    if (response && response.data) {
                        // Filter templates by platform
                        const filteredTemplates = response.data.filter(
                            template => template.platform === selectedPlatform
                        );
                        setTemplates(filteredTemplates);
                    }
                })
                .catch(err => {
                    console.error('Error fetching templates:', err);
                });
        } else {
            setTemplates([]);
        }
    }, [selectedPlatform, getTemplates]);
    
    // Extract available contact groups when transformation result changes
    useEffect(() => {
        if (transformationResult && 
            transformationResult.transformedContext && 
            transformationResult.transformedContext.availableContactGroups) {
            setAvailableContactGroups(transformationResult.transformedContext.availableContactGroups);
        } else {
            setAvailableContactGroups({});
        }
    }, [transformationResult]);
    
    // Handle platform change
    const handlePlatformChange = (value: NotificationPlatforms) => {
        setSelectedPlatform(value);
        form.setFieldsValue({ templateName: undefined });
    };
    
    // Filter scripts by selected platform
    const filteredScripts = scripts;
    
    // Handle script selection
    const handleScriptChange = (scriptId: string) => {
        const selectedScript = scripts.find(script => script.id === scriptId);
        if (selectedScript) {
            // Set sample input if available
            if (selectedScript.sampleInput) {
                try {
                    console.log('Sample result:', selectedScript.expectedOutput);
                    // Try to parse and prettify JSON to ensure it's valid and well-formatted
                    const parsedSampleInput = JSON.parse(selectedScript.sampleInput);

                    const prettifiedJson = JSON.stringify(parsedSampleInput, null, 2);
                    form.setFieldsValue({ contextJson: prettifiedJson });
                } catch (e) {
                    // If parsing fails, use the raw sampleInput as is
                    form.setFieldsValue({ contextJson: selectedScript.sampleInput });
                    console.warn('Script sampleInput is not valid JSON:', e);
                }
            }

            // Parse expectedOutput to get availableContactGroups if present
            if (selectedScript.expectedOutput) {
                try {
                    const parsedExpectedOutput = JSON.parse(selectedScript.expectedOutput);
                    if (parsedExpectedOutput.availableContactGroups) {
                        console.log('Found availableContactGroups in expectedOutput:', parsedExpectedOutput.availableContactGroups);
                        setAvailableContactGroups(parsedExpectedOutput.availableContactGroups);
                    } else {
                        // Clear contact groups if the selected script doesn't have them
                        setAvailableContactGroups({});
                    }
                } catch (e) {
                    console.warn('Script expectedOutput is not valid JSON:', e);
                    setAvailableContactGroups({});
                }
            } else {
                // Clear contact groups if no expectedOutput
                setAvailableContactGroups({});
            }
        }
    };
    
    const handleSubmit = (values: any) => {
        onSubmit(values);
    };
    
    // Initialize form with default template if available
    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
            if (initialValues.platform) {
                setSelectedPlatform(initialValues.platform);
            }
        } else {
            form.setFieldsValue({
                contextJson: JSON.stringify({
                  "user": {
                    "name": "John Doe",
                    "email": "john@example.com",
                    "phone": "+1234567890"
                  },
                  "data": {
                    "amount": 500,
                    "dueDate": "2025-07-20"
                  }
                }, null, 2)
            });
        }
    }, [form, initialValues]);
    
    // Check if we have contact groups available
    const hasContactGroups = Object.keys(availableContactGroups).length > 0;
    
    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
        >
            <Form.Item
                name="platform"
                label="Platform"
                rules={[{ required: true, message: 'Please select a platform' }]}
            >
                <Select
                    placeholder="Select platform"
                    onChange={handlePlatformChange}
                >
                    <Select.Option value="EMAIL">Email</Select.Option>
                    <Select.Option value="SMS">SMS</Select.Option>
                    <Select.Option value="WHATSAPP">WhatsApp</Select.Option>
                    <Select.Option value="IN_APP">In-App</Select.Option>
                </Select>
            </Form.Item>
            
            <Form.Item
                name="scriptId"
                label="Context Script"
                rules={[{ required: true, message: 'Please select a script' }]}
            >
                <Select
                    placeholder="Select context script"
                    disabled={!selectedPlatform}
                    onChange={handleScriptChange}
                >
                    {filteredScripts.map(script => (
                        <Select.Option key={script.id} value={script.id}>
                            {script.name} - {script.category}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            
            <Form.Item
                name="templateName"
                label="Template"
                rules={[{ required: true, message: 'Please select a template' }]}
            >
                <Select
                    placeholder="Select template"
                    disabled={!selectedPlatform}
                >
                    {templates.map(template => (
                        <Select.Option key={template.id} value={template.name}>
                            {template.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            
            <Form.Item
                name="language"
                label="Language"
                rules={[{ required: true, message: 'Please select a language' }]}
                initialValue="EN"
            >
                <Select
                    placeholder="Select language"
                    defaultValue="EN"
                >
                    <Select.Option value="EN">English</Select.Option>
                    <Select.Option value="SW">Swahili</Select.Option>
                </Select>
            </Form.Item>
            
            <Form.Item
                name="contextJson"
                label="Context JSON"
                rules={[
                    { required: true, message: 'Please enter context JSON' },
                    {
                        validator: (_, value) => {
                            try {
                                if (value) JSON.parse(value);
                                return Promise.resolve();
                            } catch (err) {
                                return Promise.reject('Invalid JSON format');
                            }
                        }
                    }
                ]}
            >
                <ContextScriptEditor 
                    value={form.getFieldValue('contextJson') || '{}'}
                    onChange={value => form.setFieldsValue({ contextJson: value })}
                    height="200px"
                />
            </Form.Item>
            
            {/* Contact Group Selection */}
            {hasContactGroups && (
                <>
                    <Typography.Title level={5}>Contact Groups</Typography.Title>
                    <Alert 
                        message="Select target contact groups for notification" 
                        description="Choose one or more contact groups that should receive this notification" 
                        type="info" 
                        showIcon 
                        className="mb-4" 
                    />
                    <Form.Item
                        name="selectedContactGroups"
                        label="Target Contact Groups"
                        rules={[{ required: true, message: 'Please select at least one contact group' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select contact groups"
                            optionLabelProp="label"
                        >
                            {Object.entries(availableContactGroups).map(([key, group]) => (
                                <Select.Option key={key} value={key} label={group.label}>
                                    <div className="flex flex-col">
                                        <span className="font-medium">{group.label}</span>
                                        <span className="text-xs text-gray-500">{group.description}</span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </>
            )}

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                    {transformationResult ? "Update Selection" : "Transform & Process"}
                </Button>
            </Form.Item>
        </Form>
    );
};
